import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE = 'https://pinballscoresapp-ayd8babtd4fpadec.eastus2-01.azurewebsites.net/api';

const Tournaments = ({ isAdmin }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [tableNames, setTableNames] = useState([]);
  const [viewingLeaderboard, setViewingLeaderboard] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [modalMessage, setModalMessage] = useState('');
  const navigate = useNavigate();

  const [newTournament, setNewTournament] = useState({
    name: '',
    tableName: '',
    tableType: '',
    type: 'Weekly',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchTournaments();
    fetchTableNames();
  }, []);

  const fetchTournaments = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tournaments?status=all`);
      setTournaments(res.data);
    } catch (err) {
      console.error('Error fetching tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableNames = async () => {
    try {
      const res = await axios.get(`${API_BASE}/tables`);
      const names = [...new Set(res.data.map(t => t.Name))];
      setTableNames(names);
    } catch (err) {
      console.error('Error fetching tables:', err);
    }
  };

  const fetchLeaderboard = async (tournamentId) => {
    setLeaderboardLoading(true);
    setViewingLeaderboard(tournamentId);
    try {
      const res = await axios.get(`${API_BASE}/tournaments/${tournamentId}?leaderboard=true`);
      setLeaderboardData(res.data);
    } catch (err) {
      console.error('Error fetching tournament leaderboard:', err);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/tournaments`, newTournament);
      setShowCreate(false);
      setNewTournament({ name: '', tableName: '', tableType: '', type: 'Weekly', startDate: '', endDate: '' });
      fetchTournaments();
    } catch (err) {
      console.error('Error creating tournament:', err);
      alert(err.response?.data?.error || 'Failed to create tournament');
    }
  };

  const confirmAndExecute = (action, message) => {
    setConfirmAction(() => action);
    setModalMessage(message);
    setModalVisible(true);
  };

  const deleteTournament = async (id) => {
    try {
      await axios.delete(`${API_BASE}/tournaments/${id}`);
      setTournaments(prev => prev.filter(t => t.TournamentID !== id));
      if (viewingLeaderboard === id) {
        setViewingLeaderboard(null);
        setLeaderboardData(null);
      }
    } catch (err) {
      console.error('Failed to delete tournament:', err);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC'
    });
  };

  const isTournamentActive = (tournament) => {
    if (!tournament.IsActive) return false;
    const now = new Date();
    const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const endDate = new Date(tournament.EndDate);
    const endUtc = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()));
    return endUtc >= todayUtc;
  };

  const activeTournaments = tournaments.filter(isTournamentActive);
  const completedTournaments = tournaments.filter(t => !isTournamentActive(t));

  const renderFormattedName = (name) => {
    if (!name || !name.includes('&')) return name;
    const parts = name.split('&');
    return parts.map((part, i) => (
      <React.Fragment key={i}>
        {part}
        {i < parts.length - 1 && (
          <span style={{ color: '#888', fontWeight: 'normal', opacity: 0.75, margin: '0 2px' }}>&amp;</span>
        )}
      </React.Fragment>
    ));
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'Weekly': return '🗓️ Weekly';
      case 'Monthly': return '📅 Monthly';
      default: return '📌 Custom';
    }
  };

  const renderTournamentTable = (list, isCompleted = false) => (
    <div className="score-table-wrapper">
      <table className="score-table">
        <thead>
          <tr>
            <th>Tournament</th>
            <th>Machine</th>
            <th>Type</th>
            <th>Dates</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map(t => (
            <tr key={t.TournamentID} style={isCompleted ? { opacity: 0.85 } : {}}>
              <td>
                {t.Name}
                {isCompleted && (
                  <span style={{
                    fontSize: '11px',
                    backgroundColor: '#888',
                    color: '#fff',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    marginLeft: '6px',
                    verticalAlign: 'middle',
                    fontFamily: 'system-ui, sans-serif'
                  }}>
                    COMPLETED
                  </span>
                )}
              </td>
              <td>
                {renderFormattedName(t.TableName)}
                {t.TableType && <span style={{ fontSize: '12px', color: '#888', marginLeft: '4px' }}>({t.TableType})</span>}
              </td>
              <td>{getTypeLabel(t.Type)}</td>
              <td style={{ fontFamily: 'system-ui, sans-serif', fontSize: '13px' }}>
                {formatDate(t.StartDate)} – {formatDate(t.EndDate)}
              </td>
              <td>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                  <button
                    onClick={() => fetchLeaderboard(t.TournamentID)}
                    style={{ fontSize: '13px', padding: '6px 12px', margin: '0' }}
                  >
                    🏆 Leaderboard
                  </button>
                  {isAdmin && (
                    <span
                      onClick={() => confirmAndExecute(
                        () => deleteTournament(t.TournamentID),
                        `Delete tournament "${t.Name}"?`
                      )}
                      title="Delete Tournament"
                      style={{ cursor: 'pointer', fontSize: '18px' }}
                    >
                      🗑️
                    </span>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const isCustom = newTournament.type === 'Custom';

  return (
    <div className="score-text">
      <h1>PinWiz Pro Tournaments</h1>

      {isAdmin && (
        <h3>
          <button className="large-button" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? 'Cancel' : '➕ Create Tournament'}
          </button>
        </h3>
      )}

      {/* Create Tournament Form */}
      {showCreate && (
        <div className="score-form fade-in">
          <h3>Create New Tournament</h3>
          <form onSubmit={handleCreate}>
            <label>
              Tournament Name:
              <input
                type="text"
                value={newTournament.name}
                onChange={e => setNewTournament(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Monster Bash Weekly Challenge"
                required
              />
            </label>
            <label>
              Machine:
              <input
                list="tournament-table-names"
                value={newTournament.tableName}
                onChange={e => setNewTournament(prev => ({ ...prev, tableName: e.target.value }))}
                required
              />
              <datalist id="tournament-table-names">
                {tableNames.map((name, i) => (
                  <option key={i} value={name} />
                ))}
              </datalist>
            </label>
            <label>
              Machine Type (optional):
              <select
                value={newTournament.tableType}
                onChange={e => setNewTournament(prev => ({ ...prev, tableType: e.target.value }))}
              >
                <option value="">All Types</option>
                <option value="Pro">Pro</option>
                <option value="Premium">Premium</option>
                <option value="LE">LE</option>
                <option value="FX">FX</option>
                <option value="VPX">VPX</option>
              </select>
            </label>
            <label>
              Tournament Type:
              <select
                value={newTournament.type}
                onChange={e => setNewTournament(prev => ({ ...prev, type: e.target.value }))}
                required
              >
                <option value="Weekly">Weekly (current week)</option>
                <option value="Monthly">Monthly (current month)</option>
                <option value="Custom">Custom Date Range</option>
              </select>
            </label>
            {isCustom && (
              <>
                <label>
                  Start Date:
                  <input
                    type="date"
                    value={newTournament.startDate}
                    onChange={e => setNewTournament(prev => ({ ...prev, startDate: e.target.value }))}
                    required={isCustom}
                  />
                </label>
                <label>
                  End Date:
                  <input
                    type="date"
                    value={newTournament.endDate}
                    onChange={e => setNewTournament(prev => ({ ...prev, endDate: e.target.value }))}
                    required={isCustom}
                  />
                </label>
              </>
            )}
            <button className="large-button" type="submit">Create Tournament</button>
          </form>
        </div>
      )}

      {/* Active Tournaments Section */}
      {loading ? (
        <h3>Loading tournaments...</h3>
      ) : activeTournaments.length === 0 ? (
        <div className="leaderboard fade-in">
          <h3>Active Tournaments</h3>
          <p style={{ fontFamily: 'system-ui, sans-serif', fontSize: '14px', color: '#888' }}>
            No active tournaments right now. {isAdmin ? 'Create one using the button above!' : 'Check back soon!'}
          </p>
        </div>
      ) : (
        <div className="leaderboard fade-in">
          <h3>Active Tournaments</h3>
          {renderTournamentTable(activeTournaments, false)}
        </div>
      )}

      {/* Completed Tournaments Collapsible Section */}
      {!loading && completedTournaments.length > 0 && (
        <div className="leaderboard fade-in" style={{ marginTop: '20px' }}>
          <div
            onClick={() => setShowCompleted(!showCompleted)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              userSelect: 'none'
            }}
          >
            <h3 style={{ margin: 0 }}>
              🏁 Completed Tournaments ({completedTournaments.length})
            </h3>
            <span style={{ fontSize: '16px', color: '#888' }}>
              {showCompleted ? '▲ Hide' : '▼ Show'}
            </span>
          </div>

          {showCompleted && (
            <div style={{ marginTop: '15px' }}>
              {renderTournamentTable(completedTournaments, true)}
            </div>
          )}
        </div>
      )}

      {/* Tournament Leaderboard */}
      {viewingLeaderboard && (
        <div className="leaderboard fade-in" style={{ marginTop: '20px' }}>
          {leaderboardLoading ? (
            <h3>Loading leaderboard...</h3>
          ) : leaderboardData ? (
            <>
              <h3>🏆 {leaderboardData.tournament.Name}</h3>
              <p style={{ fontFamily: 'system-ui, sans-serif', fontSize: '13px', color: '#666', margin: '4px 0 12px' }}>
                {renderFormattedName(leaderboardData.tournament.TableName)}
                {leaderboardData.tournament.TableType && ` (${leaderboardData.tournament.TableType})`}
                {' • '}
                {formatDate(leaderboardData.tournament.StartDate)} – {formatDate(leaderboardData.tournament.EndDate)}
              </p>
              {leaderboardData.leaderboard.length === 0 ? (
                <p style={{ color: '#888', fontFamily: 'system-ui, sans-serif' }}>No scores submitted in this tournament window yet.</p>
              ) : (
                <div className="score-table-wrapper">
                  <table className="score-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Player</th>
                        <th>Score</th>
                        <th>Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboardData.leaderboard.map((score, index) => (
                        <tr key={score.ScoreID}>
                          <td>
                            {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : index + 1}
                          </td>
                          <td>{score.Initials}</td>
                          <td>{Number(score.Score).toLocaleString()}</td>
                          <td>
                            {new Date(score.DatePlayed).toLocaleDateString('en-US', {
                              month: 'numeric',
                              day: 'numeric',
                              year: 'numeric',
                              timeZone: 'UTC'
                            })}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center' }}>
                              {score.IsVerified && <span title="Verified">✅</span>}
                              {score.ProofUrl && (
                                <a href={score.ProofUrl} target="_blank" rel="noopener noreferrer" title="View Proof" style={{ textDecoration: 'none' }}>
                                  🖼️
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button
                className="large-button"
                onClick={() => { setViewingLeaderboard(null); setLeaderboardData(null); }}
                style={{ marginTop: '10px' }}
              >
                Close Leaderboard
              </button>
            </>
          ) : null}
        </div>
      )}

      {/* Confirmation Modal */}
      {modalVisible && (
        <div className="modal-overlay">
          <div className="modal">
            <p>{modalMessage}</p>
            <button onClick={() => { confirmAction && confirmAction(); setModalVisible(false); }}>Yes</button>
            <button onClick={() => setModalVisible(false)}>Cancel</button>
          </div>
        </div>
      )}

      <button className="large-button" onClick={() => navigate('/')}>⬅️ Back to Home</button>
    </div>
  );
};

export default Tournaments;
